import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { Body, PageHeader, Row } from 'src/presentation/components/forms/Layout';
import { Button, CUSTOM_CHANNEL_ICONS } from 'src/presentation/components/agent_desktop_components';
import { CheckboxWithBoldLabel } from 'src/presentation/components/forms/Checkbox';
import ConfirmCancelModal from 'src/presentation/components/forms/ConfirmCancelModal';
import customChannelResource from 'src/actions/channels/customChannelResource';
import { DropdownMediumWidth } from 'src/presentation/components/forms/Dropdown';
import { EndpointTypes } from 'src/models/Channels/endpointType';
import EntryPointDefaults from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointDefaults';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm } from 'src/presentation/components/forms/Form';
import InputField from 'src/presentation/components/forms/InputField';
import { STATUS_PERSISTING, STATUS_SUCCEEDED, useResourceValues } from 'src/presentation/hooks/useResource';
import { StyledContainer } from 'src/presentation/components/pages/channels/entryPoints/components/EntryPointLayouts';
import useDirtyFormConfirmation from 'src/presentation/components/pages/shared/hooks/useDirtyFormConfirmation';
import useInboxes from 'src/presentation/components/pages/channels/entryPoints/hooks/useInboxes';
import { StyledLink } from 'src/presentation/components/pages/channels/shared/setupPhoneModal/templates/shared/elements';
import { getAdminURL } from 'src/presentation/components/pages/shared/utils/Utils';

const CustomChannelEditor = ({ errors, item, root }: FormEditorProps) => {
  const [isComplete, setComplete] = useState<boolean>();
  const { inboxesMap, inboxesOptions } = useInboxes();

  const { addWithDefaults: add, update, validate } = customChannelResource.effects;
  const { resetErrors } = customChannelResource.actions;

  const { persistingState } = useResourceValues(customChannelResource);

  const formContext = useForm({
    add,
    errors,
    defaultItem: {
      agentInitiatedConversations: false,
      displayAddressInProfile: true,
      type: EndpointTypes.CUSTOM_CHANNEL,
    },
    item,
    resetErrors,
    root,
    update,
    validate,
  });

  const { isNew, onClose, onSave, workingState } = formContext;

  const { handleCancel, handleStay, isConfirmCancelModalVisible, isModified } = useDirtyFormConfirmation({
    formContext,
    isComplete,
    setComplete,
  });

  useEffect(() => {
    if (persistingState === STATUS_SUCCEEDED) {
      setComplete(true);
    }
  }, [persistingState, setComplete]);

  const renderDefaultsEditor = (
    <>
      <Row>
        <EntryPointDefaultsStyled
          className="entryPointsDefaults"
          data-aid="entry-points-default"
          inboxes={inboxesOptions}
          inboxesMap={inboxesMap}
        />
      </Row>
      <Row>
        <StyledAlertInfo data-aid="alert-info-heads-up">
          <b>Heads up:&nbsp;</b>
          Once the channel is created, you can edit the Default Inbox and Entry Point SLA in the{' '}
          <StyledLink target="_blank" rel="noopener noreferrer" href={getAdminURL('entry-points')}>
            Entry Points
          </StyledLink>{' '}
          page.
        </StyledAlertInfo>
      </Row>
    </>
  );

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader
          headerText={`${isNew ? 'Add' : 'Edit'} Channel`}
          descriptionText="Assign the channel name and icon."
        />
        <Body>
          <Row>
            <InputField
              attr="name"
              data-aid="name"
              description="This name will be displayed in Gladly."
              disabled={persistingState === STATUS_PERSISTING}
              label="Channel name"
              placeholder="Enter the name"
            />
          </Row>

          <Row>
            <DropdownMediumWidth
              className="icon"
              attr="icon"
              description={
                <>
                  This icon will appear in the Customer Profile and <br />
                  Conversation Timeline.
                </>
              }
              data-aid="icon"
              disabled={persistingState === STATUS_PERSISTING}
              label="Select the channel icon"
              options={CUSTOM_CHANNEL_ICONS.getChannelIconsDropdownOptions({
                letter: workingState?.name,
              })}
              placeholder="Select an icon"
            />
          </Row>

          <Row>
            <CheckboxWithBoldLabel
              attr="displayAddressInProfile"
              data-aid="displayAddressInProfile"
              label="Display the channel in the Customer Profile"
              description="When selected, the channel icon and display name will appear in the contact details."
            />
          </Row>

          <Row>
            <CheckboxWithBoldLabel
              attr="messageGroupingEnabled"
              data-aid="messageGroupingEnabled"
              label="Activate Message Groups"
              description="When selected, agents are able to highlight inbound messages from the same group. Additionally, every inbound message must be tagged with a Group ID."
            />
          </Row>

          <Row>
            <CheckboxWithBoldLabel
              attr="agentInitiatedConversations"
              data-aid="agentInitiatedConversations"
              label="Allow agents to initiate an outbound message"
              description="When selected, agents can initiate outbound messages to the customer at any time."
            />
          </Row>

          <Row>
            <CheckboxWithBoldLabel
              attr="allowAttachments"
              data-aid="allowAttachments"
              label="Allow agents to send attachments"
              description="When selected, agents can attach files to outbound messages."
            />
          </Row>

          {isNew && renderDefaultsEditor}
        </Body>

        <Footer>
          <Button data-aid="cancel-button" type={Button.Types.TEXT} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            data-aid="save-button"
            disabled={!isModified}
            isLoading={persistingState === STATUS_PERSISTING}
            onClick={onSave}
          >
            Save
          </Button>
        </Footer>
      </Form>
      <ConfirmCancelModal
        name="Custom Channel"
        handleLeave={onClose}
        handleStay={handleStay}
        isModified={isModified && !isComplete}
        isVisible={isConfirmCancelModalVisible}
        customText="If you leave this page, your changes will be discarded."
      />
    </>
  );
};

const EntryPointDefaultsStyled = styled(EntryPointDefaults)`
  width: 100%;

  ${StyledContainer} {
    grid-template-columns: minmax(0, 250px) 120px;
  }
`;

const StyledAlertInfo = styled(AlertInfo)`
  padding: 16px;
  line-height: 20px;
  > svg {
    margin-top: 5px;
  }
`;

export default CustomChannelEditor;
